import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { DataTable } from '../../../../shared/components/DataTable.js';
import { Button } from '../../../../shared/components/Button.js';
import {
  ColumnFilters,
  type FilterableColumn,
} from '../../../../shared/components/ColumnFilters.js';
import { JobJourney } from '../../../../shared/components/JobJourney.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useElapsed } from '../../../../shared/hooks/useElapsed.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { loadMyTrackedJobs, type TrackedJob } from '../../api/jobTrackingApi.js';
import { getJobJourney, type JourneyEntry } from '../../api/jobJourneyApi.js';

/**
 * The stand-in while the first read is in flight.
 *
 * Declared once rather than written as `jobs ?? []` at the point of use:
 * `ColumnFilters` reports its surviving set through an effect keyed on the
 * rows it was given, so a fresh `[]` on every render makes that effect fire on
 * every render, which sets state, which renders again. The screen does not
 * crash — it spins, which is harder to notice and was caught only because the
 * test suite stopped returning.
 */
const NONE: readonly TrackedJob[] = Object.freeze([]);

/**
 * Job Tracking — where is my job, who is holding it, and for how long.
 *
 * WHY THIS SCREEN EXISTS
 *   The owner's rule: *"each stakeholder must know where is his job and at
 *   which level its pending and for how many days/hrs."*
 *
 *   Nine screens already list jobs and none of them answers that. The nearest
 *   miss is the approval inbox, which answers **"what must I act on"** — a
 *   different question. A Sales Officer's job sitting with the Branding Head
 *   appeared in nobody's list, least of all his, though he is the one still
 *   answerable for it.
 *
 * ONE SCREEN, EVERY ROLE
 *   Not four screens, and no branch on role anywhere in this file. The server
 *   answers for whoever is asking — a job is mine by the ownership stamped on
 *   it, or by the vendor my login belongs to — so the same table serves a Sales
 *   Officer, a Branding Officer, a Branding Head and a vendor. Four copies
 *   would drift apart the first time a stage was added to one of them.
 *
 * NOTHING ABOUT THE WORKFLOW IS WRITTEN HERE
 *   No stage names, no role list, no approval order. The stage is whatever the
 *   engine reported, and the drawing is `JobJourney`, which builds its spine
 *   from the journey it is given. A tenant that adds an approval level in the
 *   workflow designer appears correctly with nothing changed here.
 */
export function JobTrackingPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { decimal } = useBusinessNumber();
  const { exactly } = useElapsed();

  const [jobs, setJobs] = useState<readonly TrackedJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Rows left after the heading filters. Null until the panel has reported. */
  const [shown, setShown] = useState<readonly TrackedJob[] | null>(null);

  /** The job whose approval cycle is open, and the cycle itself. */
  const [openJob, setOpenJob] = useState<TrackedJob | null>(null);
  const [journey, setJourney] = useState<readonly JourneyEntry[]>([]);
  const [journeyError, setJourneyError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    loadMyTrackedJobs(token)
      .then((r) => setJobs(r.data))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, [token]);

  useEffect(load, [load]);

  // The spine is fetched only when a job is opened: a tenant with hundreds of
  // jobs should not pay for hundreds of journeys in order to draw one.
  useEffect(() => {
    if (!openJob) return;
    setJourneyError(null);
    setJourney([]);
    getJobJourney(openJob.jobRequestId, token)
      .then((r) => setJourney(r.entries ?? []))
      .catch((e: unknown) =>
        setJourneyError(e instanceof Error ? e.message : String(e)),
      );
  }, [openJob, token]);

  const rows = jobs ?? NONE;
  const visible = shown ?? rows;

  const filterColumns = useMemo<ReadonlyArray<FilterableColumn<TrackedJob>>>(
    () => [
      { key: 'dealer', label: t('jobTracking.col.dealer'), valueOf: (j) => j.customerName },
      { key: 'district', label: t('jobTracking.col.district'), valueOf: (j) => j.district },
      { key: 'status', label: t('jobTracking.col.status'), valueOf: (j) => j.currentStatus },
      // The two questions this screen exists to answer are also the two most
      // useful ways to cut the list: everything stuck at one stage, or
      // everything sitting on one role.
      { key: 'stage', label: t('jobTracking.col.stage'), valueOf: (j) => j.pendingStepName },
      { key: 'with', label: t('jobTracking.col.pendingWith'), valueOf: (j) => j.pendingRoleCode },
      { key: 'vendor', label: t('jobTracking.col.vendor'), valueOf: (j) => j.vendorName },
    ],
    [t],
  );

  const onFiltered = useCallback((v: readonly TrackedJob[]) => setShown(v), []);

  /**
   * Who is holding it: the person when the engine named one, the role when it
   * named only a role. Never left blank — a job waiting on nobody has finished
   * its approvals, and that is worth saying rather than showing a dash.
   */
  const heldBy = useCallback(
    (j: TrackedJob): string => {
      if (!j.pendingStepName) return t('jobTracking.withNobody');
      return j.pendingUserName ?? j.pendingRoleCode ?? t('jobTracking.withNobody');
    },
    [t],
  );

  const columns = useMemo(
    () => [
      {
        key: 'jobNumber',
        header: t('jobTracking.col.job'),
        sortable: true,
        render: (j: TrackedJob) => j.jobNumber ?? t('jobTracking.notYetNumbered'),
      },
      {
        key: 'customerName',
        header: t('jobTracking.col.dealer'),
        sortable: true,
        render: (j: TrackedJob) => j.customerName,
      },
      {
        key: 'district',
        header: t('jobTracking.col.district'),
        sortable: true,
        render: (j: TrackedJob) => j.district ?? '\u2014',
      },
      {
        key: 'requestDate',
        header: t('jobTracking.col.raised'),
        sortable: true,
        render: (j: TrackedJob) => day(j.requestDate),
      },
      {
        key: 'areaRequested',
        header: t('jobTracking.col.area'),
        render: (j: TrackedJob) => `${decimal(j.areaRequested)} ${unitLabel(j.areaUnit)}`,
      },
      {
        key: 'currentStatus',
        header: t('jobTracking.col.status'),
        sortable: true,
        render: (j: TrackedJob) => j.currentStatus,
      },
      {
        key: 'pendingStepName',
        header: t('jobTracking.col.stage'),
        sortable: true,
        render: (j: TrackedJob) => j.pendingStepName ?? '\u2014',
      },
      {
        key: 'pendingWith',
        header: t('jobTracking.col.pendingWith'),
        cellAriaLabel: (j: TrackedJob) => `pending-with-${j.jobRequestId}`,
        render: heldBy,
      },
      {
        // DataTable sorts on the row's own field, so this sorts on the raw
        // minutes while showing words. Sorting on "3 days" would order the
        // column alphabetically.
        key: 'waitingMinutes',
        header: t('jobTracking.col.waiting'),
        sortable: true,
        cellAriaLabel: (j: TrackedJob) => `waiting-${j.jobRequestId}`,
        render: (j: TrackedJob) => exactly(j.waitingMinutes),
      },
      {
        key: 'brandingRequestNo',
        header: t('jobTracking.col.branding'),
        render: (j: TrackedJob) => j.brandingRequestNo ?? '\u2014',
      },
      {
        key: 'vendorName',
        header: t('jobTracking.col.vendor'),
        render: (j: TrackedJob) => j.vendorName ?? '\u2014',
      },
      {
        key: 'open',
        header: '',
        render: (j: TrackedJob) => (
          <Button
            variant="secondary"
            aria-label={`open-cycle-${j.jobRequestId}`}
            onClick={() =>
              setOpenJob((cur) =>
                cur?.jobRequestId === j.jobRequestId ? null : j,
              )
            }
          >
            {openJob?.jobRequestId === j.jobRequestId
              ? t('jobTracking.hideCycle')
              : t('jobTracking.showCycle')}
          </Button>
        ),
      },
    ],
    [t, day, exactly, heldBy, openJob],
  );

  return (
    <section aria-label="job-tracking">
      <PageHeader
        title={t('jobTracking.title')}
        subtitle={t('jobTracking.subtitle')}
        data-testid="job-tracking-header"
        actions={
          <Button variant="secondary" aria-label="refresh-jobs" onClick={load}>
            {t('jobTracking.refresh')}
          </Button>
        }
      />

      {error ? (
        <p role="alert" aria-label="tracking-error">
          {error}
        </p>
      ) : null}

      <ColumnFilters
        ariaLabel="job-filters"
        columns={filterColumns}
        rows={rows}
        onChange={onFiltered}
      />

      <DataTable
        ariaLabel="tracked-jobs"
        testId="tracked-jobs"
        columns={columns}
        rows={visible}
        rowKey={(j) => j.jobRequestId}
        rowAriaLabel={(j) => `job-${j.jobRequestId}`}
        loading={jobs === null}
        emptyMessage={
          rows.length === 0
            ? t('jobTracking.noneOfMine')
            : t('jobTracking.noneMatch')
        }
        // The approval cycle opens directly under its own row, so nobody has
        // to scroll past the list to find it. Drawn rather than tabulated: the
        // table says where a job is standing; this says how it got there.
        rowDetail={(j) =>
          openJob?.jobRequestId === j.jobRequestId ? (
            <section aria-label="approval-cycle">
              <h2>
                {t('jobTracking.cycleFor')}{' '}
                {openJob.jobNumber ?? openJob.customerName}
              </h2>
              {journeyError ? (
                <p role="alert" aria-label="cycle-error">
                  {journeyError}
                </p>
              ) : null}
              <JobJourney
                steps={journey}
                emptyMessage={t('jobTracking.cycleEmpty')}
                ariaLabel="approval-journey"
              />
            </section>
          ) : null
        }
      />
    </section>
  );
}
